import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  FileText, 
  Download, 
  Eye, 
  Calendar, 
  User, 
  Shield, 
  AlertCircle, 
  Loader2,
  RefreshCw,
  Search
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { supabase } from '../../lib/supabase';
import { getUserKeyPair, decryptKeysFromSender } from '../../lib/crypto';
import { retrieveFromIPFS } from '../../lib/ipfs';
import { decryptFile } from '../../lib/encryption';
import { formatDistanceToNow } from 'date-fns';
import Card from '../atoms/Card';
import Button from '../atoms/Button';
import Input from '../atoms/Input';
import toast from 'react-hot-toast';

interface SharedDocument {
  id: string;
  document_id: string;
  document_name: string;
  document_type: string;
  document_size: number;
  shared_by_email: string;
  shared_by_name: string;
  shared_at: string;
  access_level: string;
  encrypted_key: string;
  recipient_key_fingerprint: string;
  ipfs_cid: string;
  is_encrypted: boolean;
}

const SharedWithMePage: React.FC = () => {
  const [sharedDocuments, setSharedDocuments] = useState<SharedDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [downloadingDocs, setDownloadingDocs] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const { user } = useAuthStore();

  useEffect(() => {
    if (user) {
      fetchSharedDocuments();
    }
  }, [user]);

  const fetchSharedDocuments = async () => {
    if (!user) return;

    setIsLoading(true);
    setError(null);

    try {
      // Query for documents shared with the current user
      const { data, error } = await supabase
        .from('document_shares')
        .select(`
          id,
          document_id,
          shared_by,
          shared_at,
          access_level,
          encrypted_key,
          recipient_key_fingerprint,
          documents (
            name,
            type,
            size,
            ipfs_cid,
            is_encrypted
          ),
          shared_by_user:users!shared_by (
            email,
            name
          )
        `)
        .or(`shared_with_email.eq.${user.email},shared_with_wallet.eq.${user.walletAddress || 'null'}`)
        .order('shared_at', { ascending: false });

      if (error) {
        throw new Error(error.message);
      }

      // Transform the data to a flatter structure
      const transformedData: SharedDocument[] = (data || []).map(item => ({
        id: item.id,
        document_id: item.document_id,
        document_name: item.documents?.name || 'Unknown Document',
        document_type: item.documents?.type || 'application/octet-stream',
        document_size: item.documents?.size || 0,
        shared_by_email: item.shared_by_user?.email || 'Unknown',
        shared_by_name: item.shared_by_user?.name || item.shared_by_user?.email?.split('@')[0] || 'Unknown User',
        shared_at: item.shared_at,
        access_level: item.access_level,
        encrypted_key: item.encrypted_key,
        recipient_key_fingerprint: item.recipient_key_fingerprint,
        ipfs_cid: item.documents?.ipfs_cid || '',
        is_encrypted: item.documents?.is_encrypted || false
      }));

      setSharedDocuments(transformedData);
    } catch (error: any) {
      console.error('Failed to fetch shared documents:', error);
      setError(error.message || 'Failed to load shared documents');
      toast.error('Failed to load shared documents');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDocument = async (doc: SharedDocument) => {
    setDownloadingDocs(prev => new Set(prev).add(doc.id));

    try {
      toast.loading('Preparing document...', { id: `view-${doc.id}` });

      // Step 1: Get user's private key
      const userKeyPair = await getUserKeyPair();
      if (!userKeyPair) {
        throw new Error('Your encryption keys are not available. Please log out and log back in to reinitialize them.');
      }

      // Step 2: Decrypt the document keys
      toast.loading('Decrypting access keys...', { id: `view-${doc.id}` });
      
      const decryptedKeys = await decryptKeysFromSender(
        {
          encryptedKey: doc.encrypted_key,
          encryptedIv: '', // Not used in RSA
          recipientPublicKeyFingerprint: doc.recipient_key_fingerprint
        },
        userKeyPair.privateKey
      );

      // Step 3: Retrieve encrypted document from IPFS
      toast.loading('Retrieving document from IPFS...', { id: `view-${doc.id}` });
      
      const encryptedData = await retrieveFromIPFS(doc.ipfs_cid);

      let fileBlob: Blob;

      if (doc.is_encrypted) {
        // Step 4: Decrypt the document
        toast.loading('Decrypting document...', { id: `view-${doc.id}` });
        
        const encryptedString = new TextDecoder().decode(encryptedData);
        const decryptedData = decryptFile({
          encryptedData: encryptedString,
          key: decryptedKeys.key,
          iv: decryptedKeys.iv
        });
        
        fileBlob = new Blob([decryptedData], { type: doc.document_type });
      } else {
        fileBlob = new Blob([encryptedData], { type: doc.document_type });
      }

      // Step 5: Create download
      toast.loading('Starting download...', { id: `view-${doc.id}` });
      
      const url = URL.createObjectURL(fileBlob);
      const downloadLink = document.createElement('a');
      downloadLink.href = url;
      downloadLink.download = doc.document_name;
      downloadLink.style.display = 'none';
      
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      
      URL.revokeObjectURL(url);
      
      toast.success('Document downloaded successfully!', { id: `view-${doc.id}` });

    } catch (error: any) {
      console.error('Failed to view document:', error);
      
      let errorMessage = 'Failed to access document';
      if (error.message.includes('encryption keys')) {
        errorMessage = 'Unable to decrypt document. Your encryption keys may be missing or invalid.';
      } else if (error.message.includes('IPFS')) {
        errorMessage = 'Document not available. It may have been removed from storage.';
      } else {
        errorMessage = error.message || 'Failed to access document';
      }
      
      toast.error(errorMessage, { id: `view-${doc.id}` });
    } finally {
      setDownloadingDocs(prev => {
        const newSet = new Set(prev);
        newSet.delete(doc.id);
        return newSet;
      });
    }
  };

  const formatFileSize = (bytes: number) => {
    const sizes = ['B', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 B';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round(bytes / Math.pow(1024, i) * 100) / 100} ${sizes[i]}`;
  };

  const getFileIcon = (type: string) => {
    return <FileText className="h-8 w-8 text-primary-500" />;
  };

  const filteredDocuments = sharedDocuments.filter(doc =>
    doc.document_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.shared_by_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.shared_by_email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Shared With Me</h2>
        </div>
        
        <div className="flex items-center justify-center py-12">
          <div className="text-center space-y-4">
            <Loader2 className="h-12 w-12 text-primary-500 animate-spin mx-auto" />
            <p className="text-gray-600">Loading shared documents...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Shared With Me</h2>
          <Button
            variant="outline"
            icon={RefreshCw}
            onClick={fetchSharedDocuments}
          >
            Retry
          </Button>
        </div>
        
        <Card className="p-8 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Error Loading Shared Documents
          </h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={fetchSharedDocuments} icon={RefreshCw}>
            Try Again
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
            <Users className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Shared With Me</h2>
            <p className="text-gray-600">Documents that others have securely shared with you</p>
          </div>
        </div>
        
        <Button
          variant="outline"
          icon={RefreshCw}
          onClick={fetchSharedDocuments}
          disabled={isLoading}
        >
          Refresh
        </Button>
      </div>

      {/* Search */}
      <div className="max-w-md">
        <Input
          type="text"
          placeholder="Search shared documents..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          icon={Search}
        />
      </div>

      {/* Documents Grid */}
      {filteredDocuments.length === 0 ? (
        <Card className="py-12">
          <div className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
              <Users className="h-8 w-8 text-gray-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {searchQuery ? 'No matching documents' : 'No shared documents'}
              </h3>
              <p className="text-gray-600">
                {searchQuery 
                  ? 'Try adjusting your search terms'
                  : 'Documents shared with you will appear here'
                }
              </p>
            </div>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDocuments.map((doc, index) => (
            <motion.div
              key={doc.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <Card hover className="p-6 h-full">
                <div className="space-y-4">
                  {/* Document Icon & Info */}
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      {getFileIcon(doc.document_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate" title={doc.document_name}>
                        {doc.document_name}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {formatFileSize(doc.document_size)}
                      </p>
                    </div>
                  </div>

                  {/* Security Badge */}
                  {doc.is_encrypted && (
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                        <Shield size={12} />
                        End-to-End Encrypted
                      </span>
                    </div>
                  )}

                  {/* Shared By Info */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <User size={14} />
                      <span>Shared by {doc.shared_by_name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Calendar size={14} />
                      <span>{formatDistanceToNow(new Date(doc.shared_at), { addSuffix: true })}</span>
                    </div>
                  </div>

                  {/* Access Level */}
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${
                      doc.access_level === 'read' 
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      <Eye size={12} />
                      {doc.access_level === 'read' ? 'Read Access' : doc.access_level}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="pt-4 border-t border-gray-100">
                    <Button
                      variant="primary"
                      size="sm"
                      icon={Download}
                      onClick={() => handleViewDocument(doc)}
                      loading={downloadingDocs.has(doc.id)}
                      disabled={downloadingDocs.has(doc.id)}
                      className="w-full"
                    >
                      {downloadingDocs.has(doc.id) ? 'Accessing...' : 'View Document'}
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SharedWithMePage;